import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ClassroomMeetingFormWrapperModule } from '@app/classrooms/_components';
import { ClassroomFormWrapperModule } from '@app/classrooms/_components/classroom-form-wrapper/classroom-form-wrapper.module';
import { InviteToClassroomModule } from '@app/classrooms/_components/invite-to-classroom/invite-to-classroom.module';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { FilterContentComponent } from '@app/library/panes/filter-content/filter-content.component';
import { AbstractPane } from '@app/library/panes/models/abstract-pane';
import { ClassroomMiniComponent } from '@app/library/panes/my-classes-classrooms/classroom-mini/classroom-mini.component';
import { AlertModule } from '@core/components/alert/alert.module';
import { ChapterComponent } from '@core/components/chapter/chapter.component';
import { EmptyComponent, EmptyProps } from '@core/components/empty/empty.component';
import { MeetingsTitleModule } from '@core/components/meeting-title/meetings-title.module';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import { NoContentStubComponent } from '@core/components/no-content-stub/no-content-stub.component';
import { SpinnersModule } from '@core/components/spinners/spinners.module';
import { AccountService, defaultClassroomDetails, FeaturesService } from '@core/services';
import { Store } from '@ngrx/store';

import { IconComponent } from '@shared/icon/icon.component';
import { SkeletonComponent } from '@shared/skeleton/skeleton.component';
import { getMe, getMyPins } from '@store/reducers/profile.reducer';
import {
  Classroom,
  ClassroomDetailsType,
  ClassroomParticipantRoleEnum,
  ClassroomTypeEnum,
  IFindClassroomsFilter,
  type PinnedEntity,
  PublishStatusEnum,
  SEARCH_PRESET_CLASSROOM_PARTICIPATED,
  TenantEnum,
  User,
  UserStatusEnum,
} from 'lingo2-models';
import { FormatPipeModule } from 'lingo2-ngx-date-fns';
import { InViewportModule } from 'ng-in-viewport';
import { OnUiButtonModule, OnUiCardMenuModule, OnUiCoverModule } from 'onclass-ui';
import type { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface IClassroomsTab {
  code: 'all' | 'individual' | 'group' | 'schools' | 'archive';
  title: string;
  count?: number;
}

const emptyProps: EmptyProps = {
  title: 'my-classes.no-classes.title',
  text: 'my-classes.no-classes.text',
  link: '/classes',
  link_label: 'my-classes.no-classes.link_label',
  link2: '/my-classes/classrooms',
  image: 'classrooms',
};

const emptyPropsTeacher: Partial<EmptyProps> = {
  title: 'my-classes.no-classes.title',
  text: 'my-classes.no-classes.text_teacher',
  action: true,
  link_label: 'my-classes.no-classes.link_label_teacher',
  link2: '/my-classes/classrooms',
  image: 'classrooms',
};

@Component({
  selector: 'app-my-classes-classrooms',
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,
    SpinnersModule,
    RouterModule,

    OnUiButtonModule,
    ModalModule,
    ClassroomFormWrapperModule,
    OnUiCoverModule,
    FormatPipeModule,
    IconComponent,
    OnUiCardMenuModule,
    ClassroomMeetingFormWrapperModule,
    MobileModalModule,
    InviteToClassroomModule,
    InViewportModule,
    MeetingsTitleModule,
    NoContentStubComponent,
    AlertModule,
    FilterContentComponent,
    ChapterComponent,
    EmptyComponent,
    SkeletonComponent,
    ClassroomMiniComponent,
  ],
  templateUrl: './my-classes-classrooms.component.html',
  styleUrls: ['./my-classes-classrooms.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyClassesClassroomsComponent extends AbstractPane implements OnInit, OnDestroy {
  public tabs: IClassroomsTab[] = [
    { code: 'all', title: 'my-classes.no-classes.filter.Active' },
    // { code: 'individual', title: 'Individual', count: 3 },
    // { code: 'group', title: 'Group', count: 0 },
    // { code: 'schools', title: 'Schools', count: 2 },
    { code: 'archive', title: 'my-classes.no-classes.filter.Archive' },
  ];
  public pins$: Observable<PinnedEntity[]>;
  public activeTab: IClassroomsTab;
  public classroomModalOpened: boolean;
  public classroom: Classroom = new Classroom();
  public isGuest: boolean;
  public isTeacher: boolean;
  public showEmptyContent: boolean;
  public emptyContent: Partial<EmptyProps>;
  public showSkeleton = true;

  public classroomDetails: ClassroomDetailsType[] = [
    ...defaultClassroomDetails,
    'user_service',
    'user_service_contract',
  ];
  private _me: User;

  protected store = inject(Store);
  protected features = inject(FeaturesService);

  public ngOnInit(): void {
    this.pins$ = this.store.select(getMyPins);
    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this._me = me;
          this.isGuest = me?.status === UserStatusEnum.guest;
          this.isTeacher = AccountService.isAsIfTeacher(me);
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    if (this.isMeVersion()) {
      emptyProps.link_label = null;
    }
    this.emptyContent = this.isTeacher ? emptyPropsTeacher : emptyProps;
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
  }

  public get classroomsSliced(): Classroom[] {
    return this.pane.display_mode === 'row'
      ? (this.rawContentItems as Classroom[])
      : (this.rawContentItems as Classroom[]).slice(0, 3);
  }

  public get me(): User {
    return this._me;
  }

  public hasContent(): boolean {
    return true;
  }

  public isMeVersion() {
    return this.features.tenant === TenantEnum.onclass_me;
  }

  public activateTab(tab: IClassroomsTab, force = false) {
    if (this.activeTab?.code !== tab.code || force) {
      this.activeTab = tab;
      this.setupFilter();
      this.load();
      this.detectChanges();
    }
  }

  public activateDefaultTab() {
    if (!this.activeTab) {
      this.activateTab(this.tabs[0]);
    }
  }

  public load() {
    if (!this.activeTab) {
      this.activeTab = this.tabs[0];
      this.setupFilter();
    }
    super.loadPane();
  }

  public onCreateRoom() {
    this.classroomModalOpened = true;
    this.detectChanges();
  }

  public onCloseModal() {
    this.classroomModalOpened = false;
    this.detectChanges();
  }

  public onSavedClassroom() {
    this.setupFilter();
    this.load();
    this.onCloseModal();
    this.detectChanges();
  }

  public updateList() {
    this.setupFilter();
    this.load();
    this.detectChanges();
  }

  public onEdit(classroom: Classroom) {
    this.classroom = classroom;
    this.classroomModalOpened = true;
    this.detectChanges();
  }

  public get isAsIfTeacher(): boolean {
    return AccountService.isAsIfTeacher(this._me);
  }

  protected setupFilter() {
    if (this.pane?.hidden_filter_block) {
      return;
    }
    let classroomsFilter: Partial<IFindClassroomsFilter>;
    switch (this.activeTab?.code) {
      case 'all':
        classroomsFilter = {
          ...SEARCH_PRESET_CLASSROOM_PARTICIPATED,
          role: [
            ClassroomParticipantRoleEnum.owner,
            ClassroomParticipantRoleEnum.teacher,
            ClassroomParticipantRoleEnum.student,
          ],
          publish_status: [PublishStatusEnum.published],
        };
        break;

      case 'individual':
        classroomsFilter = {
          ...SEARCH_PRESET_CLASSROOM_PARTICIPATED,
          type: ClassroomTypeEnum.individual,
          role: [
            ClassroomParticipantRoleEnum.owner,
            ClassroomParticipantRoleEnum.teacher,
            ClassroomParticipantRoleEnum.student,
          ],
          publish_status: [PublishStatusEnum.published],
        };
        break;

      case 'group':
        classroomsFilter = {
          ...SEARCH_PRESET_CLASSROOM_PARTICIPATED,
          type: ClassroomTypeEnum.group,
          role: [
            ClassroomParticipantRoleEnum.owner,
            ClassroomParticipantRoleEnum.teacher,
            ClassroomParticipantRoleEnum.student,
          ],
          publish_status: [PublishStatusEnum.published],
        };
        break;

      case 'schools':
        classroomsFilter = {
          ...SEARCH_PRESET_CLASSROOM_PARTICIPATED,
          role: [
            ClassroomParticipantRoleEnum.owner,
            ClassroomParticipantRoleEnum.teacher,
            ClassroomParticipantRoleEnum.student,
          ],
          publish_status: [PublishStatusEnum.published],
          school_id: Classroom.IN_ANY_SCHOOL,
        };
        break;

      case 'archive':
        classroomsFilter = {
          ...SEARCH_PRESET_CLASSROOM_PARTICIPATED,
          role: [
            ClassroomParticipantRoleEnum.owner,
            ClassroomParticipantRoleEnum.teacher,
            ClassroomParticipantRoleEnum.student,
          ],
          publish_status: PublishStatusEnum.archive,
        };
        break;

      default:
        break;
    }

    if (classroomsFilter) {
      this._pane = {
        ...this._pane,
        classroomsFilter,
        loaded: false,
        classrooms: [],
      };
    }
  }

  protected onContentLoaded() {
    this.activateDefaultTab();
    this.isVisible = true;
    this.showEmptyContent = !(this.rawContentItems as Classroom[]).length && this.pane.display_mode !== 'row';
    this.showSkeleton = false;
    this.detectChanges();
  }

  public onInViewportBottomChange(reached) {
    if (reached.visible) {
      this.loadNextPage();
    }
  }

  public trackByFn(index) {
    return index;
  }
}
